import {
  usePersistentUserChoices,
  usePreviewTracks,
} from "@livekit/components-react";
import { useMeeting } from "../../../hooks/meeting";
import classNames from "./WaitingRoom.module.css";
import useUser from "../../../hooks/user";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ParticipantPlaceholder from "./MeetingParticipantPlaceholder";
import UserLanguageSelect from "../../common/UserLanguageSelect";
import { IoEllipsisHorizontal } from "react-icons/io5";
import {
  useAudioEnabled,
  useBlurFilter,
  useVideoEnabled,
} from "../../../hooks/media";
import VideoBackgrounds from "./VideoBackgrounds";
import Loader from "../../common/Loader";
import { useRoom, useRoomId } from "../../context/room/room";
import SettingsDialog from "../../common/dialogs/SettingsDialog";
import api from "../../../hooks/api-new";
import { useAxios } from "../../../hooks/axios";
import { AxiosError } from "axios";
import { Id as ToastId, toast } from "react-toastify";
import MicToggle from "../../common/MicToggle";
import CameraToggle from "../../common/CameraToggle";
import Button from "../../common/Button";
import { ParticipantMetadata } from "./MeetingParticipant";
import MediaPreviewErrorDialog from "../../common/dialogs/MediaPreviewErrorDialog";
import {
  LocalAudioTrack,
  LocalVideoTrack,
  Track,
  getBrowser,
} from "livekit-client";
import { useIsMobile } from "../../../hooks/layout";

export type WaitingRoomChoices = {
  audio: boolean;
  video: boolean;
};

export default function WaitingRoom(props: {
  choices: WaitingRoomChoices;
  onJoin: (choices: WaitingRoomChoices) => void;
}) {
  const [, setMeeting] = useMeeting();
  const [user, setUser] = useUser();

  const { room, loading } = useRoom();

  const { saveUsername } = usePersistentUserChoices();

  const [audioEnabled] = useAudioEnabled();
  const [videoEnabled] = useVideoEnabled();
  const [username, setUsername] = useState(user.auth?.name ?? "Guest");

  useEffect(() => {
    saveUsername(username);
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        name: username,
      },
    }));
  }, [username, saveUsername, setUser]);

  const [mediaErrorDialogOpen, setMediaErrorDialogOpen] = useState(false);
  const onError = useCallback(
    (error: Error) => {
      setMediaErrorDialogOpen(true);
      console.warn(error);
    },
    [setMediaErrorDialogOpen]
  );

  const { userChoices: initialUserChoices } = usePersistentUserChoices();
  const tracks = usePreviewTracks(
    {
      audio: { deviceId: initialUserChoices.audioDeviceId },
      // Re-acquire only on video enabled/disabled
      video: videoEnabled
        ? { deviceId: initialUserChoices.videoDeviceId }
        : false,
    },
    onError
  );

  const localAudioTrackPreview = useMemo(
    () =>
      tracks?.filter(
        (track) => track.kind === Track.Kind.Audio
      )[0] as LocalAudioTrack,
    [tracks]
  );

  const videoEl = useRef(null);
  const localVideoTrackPreview = useMemo(
    () =>
      tracks?.filter(
        (track) => track.kind === Track.Kind.Video
      )[0] as LocalVideoTrack,
    [tracks]
  );

  useBlurFilter(localVideoTrackPreview);

  const [meeting] = useMeeting();

  useEffect(() => {
    const el = videoEl.current;
    if (el && localVideoTrackPreview && !meeting.media.applyingBackground) {
      localVideoTrackPreview.unmute();
      localVideoTrackPreview.attach(el);
    }

    return () => {
      if (el) {
        localVideoTrackPreview?.detach(el);
      }
    };
  }, [localVideoTrackPreview, meeting.media.applyingBackground]);

  const axios = useAxios();
  const getToken = api.useGenerateLivekitToken(axios);
  const roomId = useRoomId();

  const waitingForHostToastRef = useRef<ToastId>();
  const [waitingForHost, setWaitingForHost] = useState(false);

  const browser = useMemo(() => getBrowser(), []);
  const isMobile = useIsMobile();

  const getTokenAndJoin = useCallback(async () => {
    await getToken({
      room: roomId,
      identity: user.livekitId,
    });
    toast.dismiss(waitingForHostToastRef.current);
    props.onJoin({
      audio: audioEnabled,
      video: videoEnabled,
    });
    setMeeting((prevState) => ({ ...prevState, joined: true }));
  }, [
    getToken,
    props,
    roomId,
    setMeeting,
    user.livekitId,
    audioEnabled,
    videoEnabled,
  ]);

  useEffect(() => {
    let getTokenInterval: NodeJS.Timeout | undefined = undefined;
    if (waitingForHost) {
      getTokenInterval = setInterval(async () => {
        try {
          await getTokenAndJoin();
        } catch {
          // Ignore 403 if still waiting for host
        }
      }, 5 * 1000);
    }

    return () => {
      clearInterval(getTokenInterval);
    };
  }, [waitingForHost, getTokenAndJoin]);

  if (!room || loading) {
    return <Loader />;
  }

  return (
    <>
      <div className={classNames.WaitingRoom}>
        <h1>Ready to Join?</h1>

        <div className={classNames.PreviewAndSettings}>
          <div className={classNames.Preview}>
            <div className={classNames.VideoPreview}>
              <ParticipantMetadata
                language={user.metadata.selectedLanguage}
                name={username}
                timezone={user.metadata.timezone}
                micEnabled={audioEnabled}
              />
              {localVideoTrackPreview && videoEnabled ? (
                <video ref={videoEl} width="1280" height="720" />
              ) : (
                <ParticipantPlaceholder name={username} />
              )}
              <div className={classNames.PreviewControls}>
                <MicToggle initialState={true} track={localAudioTrackPreview} />
                <CameraToggle
                  initialState={true}
                  track={localVideoTrackPreview}
                />
                <SettingsDialog
                  trigger={
                    <button>
                      <IoEllipsisHorizontal />
                    </button>
                  }
                  localAudioTrack={localAudioTrackPreview}
                  localVideoTrack={localVideoTrackPreview}
                  hiddenCategories={["text-translation"]}
                />
              </div>
            </div>
            {videoEnabled && (
              <h2>✨ Looking sharp! Ready to shine in the spotlight! ✨</h2>
            )}
          </div>

          <div className={classNames.PreviewSettings}>
            <div className={classNames.Settings}>
              <div>
                <label>Name</label>
                <input
                  type="text"
                  defaultValue={username}
                  placeholder={"Enter your name"}
                  onChange={(inputEl) => setUsername(inputEl.target.value)}
                  autoComplete="off"
                />
              </div>
              <div>
                <label>I speak</label>
                <UserLanguageSelect
                  color="white"
                  borderColor="dark-gray"
                  backgroundColor="dark-gray"
                  languages={room.languages}
                />
              </div>
              {browser?.name === "Chrome" && !isMobile && (
                <div>
                  <label>Add filter</label>
                  <VideoBackgrounds />
                </div>
              )}
            </div>
            <Button
              onClick={async () => {
                try {
                  await getTokenAndJoin();
                } catch (error) {
                  if (error instanceof AxiosError) {
                    if (error.status === 403) {
                      setWaitingForHost(true);
                      if (!waitingForHostToastRef.current) {
                        waitingForHostToastRef.current = toast(
                          <div className={classNames.Toast}>
                            {"Waiting for the host to admit you..."}
                          </div>,
                          { autoClose: false }
                        );
                      }
                    }
                  }
                }
              }}
            >
              Join now
            </Button>
          </div>
        </div>
      </div>
      <MediaPreviewErrorDialog
        open={mediaErrorDialogOpen}
        onOpenChange={setMediaErrorDialogOpen}
      />
    </>
  );
}
